"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { RIMANDO } from "@/components/home/stile";
import { SchedaDossier } from "@/components/report/SchedaDossier";
import type { SchedaReport } from "@/lib/report-dati";
import { hrefReport, type StatoReport } from "@/lib/report-raccolta";

/* L'archivio di /report che si allunga scorrendo (21/10/2026, richiesta
   del committente: «si fa vedere tutti i card», come il rullo). La prima
   pagina arriva dal server; le altre le chiede questo componente a
   `/api/report` quando la sentinella in fondo alla griglia arriva a 1.500
   pixel dalla finestra, lo stesso margine di `ElencoRisultati` del rullo.
   Lo schema delle schede resta quello delle pagine: ogni cinque, la prima
   è grande. Senza JavaScript resta «Mostra altri report», un collegamento
   alla pagina dopo; dopo un errore lo scorrimento si ferma e il pulsante
   diventa «Riprova». */

const GRIGLIA = "grid grid-cols-1 gap-7 min-[901px]:grid-cols-3";

export function ArchivioContinuo({
  stato,
  iniziali,
  pagine: pagineIniziali,
}: {
  stato: StatoReport;
  iniziali: SchedaReport[];
  pagine: number;
}) {
  const [altre, setAltre] = useState<SchedaReport[]>([]);
  const [pagina, setPagina] = useState(stato.page);
  const [pagine, setPagine] = useState(pagineIniziali);
  const [carico, setCarico] = useState<"fermo" | "caricando" | "errore">("fermo");
  const inVolo = useRef(false);
  const paginaRef = useRef(pagina);
  const pagineRef = useRef(pagine);

  const visti = new Set<string>();
  const schede = [...iniziali, ...altre].filter((s) => (visti.has(s.id) ? false : (visti.add(s.id), true)));
  const finito = pagina >= pagine;

  function carica() {
    if (inVolo.current || paginaRef.current >= pagineRef.current) return;
    inVolo.current = true;
    setCarico("caricando");
    const href = hrefReport(stato, { page: paginaRef.current + 1 });
    const qs = href.includes("?") ? href.slice(href.indexOf("?") + 1) : "";
    fetch(`/api/report?${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { schede: SchedaReport[]; pagine: number }) => {
        setAltre((a) => [...a, ...d.schede]);
        setPagine(d.pagine);
        // Un blocco vuoto chiude la lista invece di chiedere pagine vuote.
        if (d.schede.length) setPagina((p) => p + 1);
        else setPagine(paginaRef.current);
        setCarico("fermo");
      })
      .catch(() => setCarico("errore"))
      .finally(() => {
        inVolo.current = false;
      });
  }
  const caricaRef = useRef(carica);
  useEffect(() => {
    paginaRef.current = pagina;
    pagineRef.current = pagine;
    caricaRef.current = carica;
  });

  const sentinella = useRef<HTMLDivElement>(null);
  const fermo = carico === "errore";
  useEffect(() => {
    const el = sentinella.current;
    if (!el || fermo) return;
    const io = new IntersectionObserver(
      (voci) => {
        if (voci.some((v) => v.isIntersecting)) caricaRef.current();
      },
      { rootMargin: "0px 0px 1500px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [fermo, pagina]);

  return (
    <>
      <div data-rep-griglia className={`mt-7 ${GRIGLIA}`}>
        {schede.map((s, i) => (
          <SchedaDossier key={s.id} s={s} grande={i % 5 === 0} />
        ))}
      </div>
      <div ref={sentinella} aria-hidden />
      {!finito ? (
        <div className="mt-7 flex items-center justify-center border-t border-b border-t-ink border-b-rule py-3.5">
          <Link
            href={hrefReport(stato, { page: pagina + 1 })}
            scroll={false}
            onClick={(e) => {
              e.preventDefault();
              if (carico === "errore") setCarico("fermo");
              carica();
            }}
            className={`${RIMANDO} font-sans`}
            aria-busy={carico === "caricando" || undefined}
          >
            {carico === "caricando"
              ? "Carico altri report…"
              : carico === "errore"
                ? "Non hanno risposto. Riprova"
                : "Mostra altri report"}{" "}
            <span aria-hidden>↓</span>
          </Link>
        </div>
      ) : null}
      <p role="status" className="sr-only">
        {schede.length} report in pagina
      </p>
    </>
  );
}
